import { Modal } from '@lume/ui-components';
import { FileText } from 'lucide-react';
import { useState } from 'react';

import { extractTemplateVars, splitTemplate } from '../../proactive/proactive-adapter';
import { useApprovedTemplates } from '../hooks/use-approved-templates';

import type { ProactiveTemplate } from '../../proactive/types';

import '../../proactive/iniciar-conversa-2b.css';

export interface TemplateSendRequest {
  templateName: string;
  /** Posicional: índice 0 = `{{1}}`. Buracos viram '' (Engine valida required). */
  variables: string[];
  /** Preview renderizado client-side — vira o `content` persistido/otimista. */
  renderedContent: string;
}

/** Substitui `{{N}}` por valores — espelha o render do proativo (service:291). */
function renderContent(bodyPreview: string, vars: Record<string, string>): string {
  return bodyPreview.replace(/\{\{(\d+)\}\}/g, (match, idx: string) => vars[idx] ?? match);
}

/**
 * TemplateSelectorModal — Item 27 (Brief 2A §3.7 + Brief 2B Tela 8 Step 3).
 *
 * Seleção de template HSM aprovado + variáveis + preview, para reengajar em
 * janela 24h fechada. Reusa o padrão visual `.tpl-card` da Tela 8 (CSS
 * importado explicitamente — não depende do import da IniciarConversaPage).
 * Botão de envio próprio no corpo (o Modal DS não expõe confirmDisabled).
 */
export function TemplateSelectorModal({
  open,
  onClose,
  onSend,
  isSending,
}: {
  open: boolean;
  onClose: () => void;
  onSend: (req: TemplateSendRequest) => void;
  isSending: boolean;
}): JSX.Element | null {
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [vars, setVars] = useState<Record<string, string>>({});
  const { data, isLoading, isError } = useApprovedTemplates(open);

  if (!open) return null;

  const templates = data ?? [];
  const selected = templates.find((t) => t.name === selectedName);
  const varKeys = extractTemplateVars(selected?.bodyPreview);
  const allFilled = varKeys.every((k) => (vars[k] ?? '').trim().length > 0);
  const canConfirm = selected !== undefined && allFilled && !isSending;

  const onSelect = (t: ProactiveTemplate): void => {
    setSelectedName(t.name);
    setVars({});
  };

  const onConfirm = (): void => {
    if (selected === undefined || !canConfirm) return;
    const lastKey = varKeys[varKeys.length - 1];
    const maxIdx = lastKey !== undefined ? Number(lastKey) : 0;
    const variables = Array.from({ length: maxIdx }, (_, i) => vars[String(i + 1)] ?? '');
    // Sem bodyPreview (degradação D9 da Tela 8): content honesto = nome do
    // template; o cliente recebe o renderedBody server-side de qualquer forma.
    const renderedContent =
      selected.bodyPreview !== undefined && selected.bodyPreview !== ''
        ? renderContent(selected.bodyPreview, vars)
        : `[template] ${selected.name}`;
    onSend({ templateName: selected.name, variables, renderedContent });
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Enviar template aprovado"
      cancelLabel="Fechar"
      maxWidth="520px"
    >
      <div className="space-y-3 text-sm">
        <p className="text-xs" style={{ color: 'var(--fg-2)' }}>
          A janela de 24h do WhatsApp está fechada — só templates aprovados pela Meta são entregues.
          A conversa reabre quando o cliente responder.
        </p>
        {isLoading && <div className="muted text-xs">Carregando templates…</div>}
        {isError && <div className="muted text-xs">Não foi possível carregar templates.</div>}
        {!isLoading && !isError && templates.length === 0 && (
          <div className="form-row__note">
            Nenhum template aprovado neste tenant. Solicite ao gestor (Configurações → Templates).
          </div>
        )}
        {templates.length > 0 && (
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            {templates.map((t) => (
              <div
                key={t.name}
                className={'tpl-card' + (selectedName === t.name ? ' tpl-card--selected' : '')}
                onClick={() => onSelect(t)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onSelect(t);
                }}
              >
                <div className="tpl-card__name">
                  <FileText size={11} />
                  {t.name}
                  {t.category !== undefined && (
                    <span className="muted" style={{ marginLeft: 'auto', fontSize: 10 }}>
                      {t.category}
                    </span>
                  )}
                </div>
                {selectedName === t.name && (
                  <div className="tpl-card__preview">
                    {splitTemplate(t.bodyPreview, vars).map((p, i) =>
                      p.type === 'var' ? (
                        <span key={i} className="tpl-card__preview-var">
                          {p.value}
                        </span>
                      ) : (
                        <span key={i}>{p.value}</span>
                      ),
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {selected !== undefined && varKeys.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 'var(--space-2)',
            }}
          >
            {varKeys.map((k) => (
              <label key={k} className="text-xs" style={{ display: 'grid', gap: 2 }}>
                <span className="mono muted">{`{{${k}}}`}</span>
                <input
                  className="input"
                  aria-label={`Variável {{${k}}}`}
                  value={vars[k] ?? ''}
                  onChange={(e) => setVars((v) => ({ ...v, [k]: e.target.value }))}
                />
              </label>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={!canConfirm}
            onClick={onConfirm}
            title={
              canConfirm
                ? 'Enviar como template HSM'
                : 'Selecione um template e preencha todas as variáveis'
            }
          >
            {isSending ? 'Enviando…' : 'Enviar template'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
